/**
 * ARQUIVO CORRIGIDO E FINAL
 * Caminho: src/App.js
 * Descrição: O roteador foi refatorado para ter um <BrowserRouter> estável no topo da árvore de componentes,
 * prevenindo recarregamentos em loop em rotas públicas. A lógica manual de roteamento foi convertida
 * para rotas padrão do React Router para maior estabilidade e previsibilidade.
 */
import React, { useContext, useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useParams, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, AuthContext } from './contexts/AuthContext';
import { auth } from './firebase/config';
import Header from './components/ui/Header';
import LoadingSpinner from './components/ui/LoadingSpinner';
import { PatientProvider } from './contexts/PatientContext';
import { SuperviseeProvider } from './contexts/SuperviseeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { TranscriptionProvider } from './contexts/TranscriptionContext';
import { Toaster } from 'react-hot-toast';

// Lazy-loaded components and routes
const LoginScreen = lazy(() => import('./features/Auth/LoginScreen'));
const UnifiedDashboard = lazy(() => import('./features/Dashboard/UnifiedDashboard'));
const PatientPortal = lazy(() => import('./features/PatientPortal/PatientPortal'));
const PublicIntakeForm = lazy(() => import('./features/Public/PublicIntakeForm'));
const PublicClinicalForm = lazy(() => import('./features/Public/PublicClinicalForm'));
const PublicInformantForm = lazy(() => import('./features/Public/PublicInformantForm'));
const InteractiveToolPlayer = lazy(() => import('./features/PatientPortal/InteractiveToolPlayer'));
const GoogleAuthCallback = lazy(() => import('./features/Auth/GoogleAuthCallback'));
const ReportWorkspace = lazy(() => import('./features/Reports/ReportWorkspace'));
const PatientToolHistoryPage = lazy(() => import('./features/PatientPortal/PatientToolHistoryPage'));
const PublicToolPlayer = lazy(() => import('./features/Public/PublicToolPlayer'));
const SofiaSwitcher = lazy(() => import('./components/sofia2/SofiaSwitcher'));
const LaudoJobsDock = lazy(() => import('./components/laudos/LaudoJobsDock'));
const TeleconsultationRoomHost = lazy(() => import('./features/Teleconsultation/TeleconsultationRoomHost'));
const DSM5TRReport = lazy(() => import('./features/Tests/DSM5TRReport'));
const TestPlayerHost = lazy(() => import('./features/Tests/TestPlayerHost'));
const PublicSupervisionForm = lazy(() => import('./features/Supervision/PublicSupervisionForm'));
const PublicSupervisionIntake = lazy(() => import('./features/Supervision/PublicSupervisionIntake'));
const ShortLinkResolver = lazy(() => import('./features/Public/ShortLinkResolver'));
const TeleShortLinkResolver = lazy(() => import('./features/Public/TeleShortLinkResolver'));

const PublicClinicalFormWrapper = () => {
    const { submissionId } = useParams();
    return <PublicClinicalForm submissionId={submissionId} />;
};

const PublicInformantFormWrapper = () => {
    const { submissionId } = useParams();
    return <PublicInformantForm submissionId={submissionId} />;
};

const PublicIntakeFormWrapper = () => {
    const { tokenId } = useParams();
    return <PublicIntakeForm tokenId={tokenId} />;
};

const PublicToolPlayerWrapper = () => {
    const { templateId, patientId } = useParams();
    return <PublicToolPlayer templateId={templateId} patientId={patientId} />;
};

const isTeleShortPath = (pathname) => /^\/\d{2,7}\/?$/.test(pathname || '');

const AppRouter = () => {
    const { user, userData, loading } = useContext(AuthContext);
    const location = useLocation();

    // Link curto da teleconsulta (ex.: /1872) — só números, não afeta outras rotas
    if (isTeleShortPath(location.pathname)) {
        return (
            <Suspense fallback={<LoadingSpinner fullPage={true} />}>
                <TeleShortLinkResolver code={location.pathname.replace(/\D/g, '')} />
            </Suspense>
        );
    }

    return (
        <Suspense fallback={<LoadingSpinner fullPage={true} />}>
            <Routes>
                <Route path="/clinical-form/fill/:submissionId" element={<PublicClinicalFormWrapper />} />
                <Route path="/clinical-form/informant/:submissionId" element={<PublicInformantFormWrapper />} />
                <Route path="/intake/fill/:tokenId" element={<PublicIntakeFormWrapper />} />
                <Route path="/google-auth-callback" element={<GoogleAuthCallback />} />
                <Route path="/tool/public/:templateId/:patientId/:sessionId?" element={<PublicToolPlayerWrapper />} />
                <Route path="/supervision/form/:superviseeId" element={<PublicSupervisionForm />} />
                <Route path="/supervision/join/:supervisorId" element={<PublicSupervisionIntake />} />
                <Route path="/test/player/:assignmentId" element={<TestPlayerHost />} />
                <Route path="/f/:code" element={<ShortLinkResolver />} />
                <Route path="/teleconsulta/:roomId" element={<TeleconsultationRoomHost />} />
                <Route path="/tele/:roomId" element={<TeleconsultationRoomHost />} />
                <Route path="/t/:roomId" element={<TeleconsultationRoomHost />} />
                <Route element={<AuthWall user={user} userData={userData} />}>
                    <Route path="/login" element={<LoginScreen />} />
                    <Route element={<PrivateRoute user={user} userData={userData} loading={loading} />}>
                        <Route path="/*" element={<MainApp />} />
                    </Route>
                </Route>
            </Routes>
        </Suspense>
    );
};

// Sessão anônima (links públicos de teste) não conta como login no sistema
const isRealLogin = (user, userData) => Boolean((user && !user.isAnonymous) || (userData && !userData.isAnonymous));

const AuthWall = ({ user, userData }) => {
    const location = useLocation();
    const isLogin = location.pathname === '/login' || location.pathname === '/login/';
    if (isRealLogin(user, userData) && isLogin) {
        const from = location.state?.from?.pathname || '/';
        return <Navigate to={from} replace />;
    }
    return <Outlet />;
};

const PrivateRoute = ({ user, userData, loading }) => {
    const location = useLocation();
    if (loading) {
        return <LoadingSpinner fullPage={true} />;
    }
    if (!isRealLogin(user, userData)) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return <Outlet />;
};

const FUNCTIONS_BASE = 'https://southamerica-east1-pro-saude-mental.cloudfunctions.net';

const MainApp = () => {
    const { userData } = useContext(AuthContext);
    const autoClassifiedHour = useRef(-1);

    // Agendamento global: classificar leads a cada 2h das 6h às 22h
    useEffect(() => {
        const targetHours = new Set([6, 8, 10, 12, 14, 16, 18, 20, 22]);

        const runAutoClassify = async () => {
            try {
                const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
                await fetch(`${FUNCTIONS_BASE}/autoClassifyConversations`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify({}),
                });
            } catch (err) {
                console.error('Erro na classificação agendada:', err);
            }
        };

        const checkSchedule = () => {
            const hour = new Date().getHours();
            if (targetHours.has(hour) && autoClassifiedHour.current !== hour) {
                autoClassifiedHour.current = hour;
                // Trava entre abas: só uma aba aberta dispara a classificação em cada horário
                const now = new Date();
                const slotKey = `psm_autoclassify_${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}-${hour}`;
                try {
                    if (localStorage.getItem('psm_autoclassify_last') === slotKey) return;
                    localStorage.setItem('psm_autoclassify_last', slotKey);
                } catch (e) { /* storage indisponível: segue o comportamento anterior */ }
                runAutoClassify();
            }
        };

        checkSchedule();
        const interval = setInterval(checkSchedule, 60000);
        return () => clearInterval(interval);
    }, []);

    // Conta sem cadastro no sistema (ex.: login com Google de alguém que não é da equipe)
    const isAdminEmail = userData?.email && ['fabiocarmopsi@gmail.com', 'alicecarmopsi@gmail.com'].includes(userData.email.toLowerCase().trim());
    if (userData.role === 'SemAcesso' && !isAdminEmail) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4">
                <div className="max-w-sm w-full bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-white/10 p-6 text-center space-y-4">
                    <h1 className="text-lg font-semibold text-slate-900 dark:text-white">Acesso ainda não liberado</h1>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                        {userData.accessCheckFailed
                            ? 'Não foi possível confirmar o seu cadastro agora. Recarregue a página em instantes.'
                            : `A conta ${userData.email || ''} não está cadastrada na equipe. Peça ao administrador da clínica para liberar o seu acesso.`}
                    </p>
                    <button
                        onClick={() => auth.signOut().then(() => { window.location.href = '/login'; })}
                        className="w-full py-2.5 rounded-xl bg-brand-core text-white text-sm font-semibold cursor-pointer"
                    >
                        Sair e entrar com outra conta
                    </button>
                </div>
            </div>
        );
    }

    if (userData.role === 'Paciente') {
        return (
            <Suspense fallback={<LoadingSpinner fullPage={true} />}>
                <PatientPortal patient={userData} />
            </Suspense>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            <main className="max-w-screen-2xl mx-auto">
                <Suspense fallback={<LoadingSpinner fullPage={true} />}>
                    <Routes>
                        <Route path="/report/workspace/:patientId/:reportId" element={<ReportWorkspace />} />
                        <Route path="/report/dsm5tr/:patientId/:assignmentId" element={<DSM5TRReport />} />
                        <Route path="/tool/play/:templateId/:patientId" element={<InteractiveToolPlayer />} />
                        <Route path="/tool/history/:templateId/:patientId" element={<PatientToolHistoryPage />} />
                        <Route path="/*" element={<UnifiedDashboard user={userData} initialPath={window.location.pathname} />} />
                    </Routes>
                </Suspense>
            </main>
            {userData.role !== 'Paciente' && (
                <Suspense fallback={null}>
                    <SofiaSwitcher />
                    <LaudoJobsDock />
                </Suspense>
            )}
        </div>
    );
};

export default function App() {
    return (
        <NotificationProvider>
            <TranscriptionProvider>
                <AuthProvider>
                    <PatientProvider>
                        <SuperviseeProvider>
                            <BrowserRouter>
                                <AppRouter />
                                <Toaster
                                    position="top-center"
                                    reverseOrder={false}
                                    containerStyle={{ zIndex: 999999 }}
                                    toastOptions={{
                                        style: {
                                            zIndex: 999999,
                                            borderRadius: 14,
                                            fontSize: 14,
                                            padding: '12px 16px',
                                            boxShadow: '0 10px 30px -10px rgba(15, 23, 42, 0.25)',
                                            border: '1px solid rgba(148, 163, 184, 0.2)',
                                        },
                                    }}
                                />
                            </BrowserRouter>
                        </SuperviseeProvider>
                    </PatientProvider>
                </AuthProvider>
            </TranscriptionProvider>
        </NotificationProvider>
    );
}
